import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

type UseCardDBInfiniteScrollOptions = {
  hasNextPage: () => boolean;
  fetchNextPage: () => void;
  /** When this changes, scroll resets to top (e.g. filters) */
  resetOnChange?: unknown;
  /** Current page — re-triggers load when sentinel stays visible after a fetch */
  page: number;
};

export function useCardDBInfiniteScroll({
  hasNextPage,
  fetchNextPage,
  resetOnChange,
  page,
}: UseCardDBInfiniteScrollOptions) {
  const [scrollRoot, setScrollRoot] = useState<Element | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const hasNextPageRef = useRef(hasNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);

  hasNextPageRef.current = hasNextPage;
  fetchNextPageRef.current = fetchNextPage;

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  const { ref: sentinelRef, inView } = useInView({
    root: scrollRoot,
    rootMargin: "200px 0px",
    threshold: 0,
    skip: scrollRoot === null,
  });

  // Reset isFetching when page changes (meaning fetch completed)
  useEffect(() => {
    if (page > 1) {
      setIsFetching(false);
    }
  }, [page]);

  useEffect(() => {
    const hasNext = hasNextPageRef.current();

    if (!scrollRoot || !inView || !hasNext || isFetching) {
      return;
    }

    setIsFetching(true);
    fetchNextPageRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, scrollRoot]);

  useEffect(() => {
    if (scrollRoot instanceof HTMLDivElement) {
      scrollRoot.scrollTop = 0;
    }
    setIsFetching(false);
  }, [resetOnChange, scrollRoot]);

  return { scrollRef, sentinelRef };
}
